import { getToken } from '../../components/authentication/functions/authentication_management';
import axios from 'axios';
import { validate as validateUuid } from 'uuid';
import { AppError, StructuredError } from '../../types/error.d';
import type { PashaBulkaRunSettings } from '../../types/simulation';
import { getDefaultRequestTime } from '../../utils/dateTimeUtils';
import { loggingService } from '../logging/loggingService';
import { getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';

const API_BASE_URL = getSimulationApiBaseUrl();

/** Shown when a Liftango API call fails due to network/connection. Access to APIs often requires Tailscale or org VPN. */
const NETWORK_OR_ACCESS_HOW_TO_FIX =
  'If your internet works but you still see this, access to Liftango APIs may require Tailscale or your organisation\'s VPN. Connect to Tailscale (or VPN) and try again. If the problem persists, contact support.';

function randomUuidString(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** One row POSTed to `/data_generator/import_rides/{name}` (snake_case, Liftango-validated). */
export type ImportRidesApiRideRow = {
  ride_id?: string;
  external_reference_id: string;
  external_user_reference_id: string;
  from_latitude: number;
  from_longitude: number;
  to_latitude: number;
  to_longitude: number;
  from_address: string;
  to_address: string;
  request_type: string;
  passengers: unknown;
  batch_id: string;
  request_time: string;
};

export type FormatImportRidesUploadOptions = {
  sharedBatchId: string;
  /** When true, include UUID `ride_id` in import payload. Defaults to false to avoid backend uniqueness collisions on re-upload. */
  includeRideId?: boolean;
  /**
   * When no external ref / ride id string exists on a row (rare), supply a UUID.
   * Tests should pass a deterministic function.
   */
  externalRefFallback?: () => string;
};

/**
 * Maps app {@link RideSchema}-like rows to the Liftango import-rides body.
 * Human / row trip keys go to `external_reference_id`; `ride_id` is only sent when it is a valid UUID.
 */
export function formatRidesForImportRidesUpload(
  rides: any[],
  options: FormatImportRidesUploadOptions
): ImportRidesApiRideRow[] {
  const sharedBatchId = options.sharedBatchId;
  const includeRideId = options.includeRideId ?? false;
  const externalRefFallback = options.externalRefFallback ?? randomUuidString;

  const asNonEmptyIdString = (value: unknown): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') {
      const t = value.trim();
      return t.length > 0 ? t : undefined;
    }
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return undefined;
  };

  const asFiniteNumber = (value: unknown): number | null => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const normalizePassengers = (ride: any): Array<{ type: string; number_of_passengers: number }> => {
    if (Array.isArray(ride?.passengers) && ride.passengers.length > 0) {
      const normalized = ride.passengers
        .map((p: any) => ({
          type: String(p?.type ?? p?.passenger_type ?? 'standard').toLowerCase() === 'wheelchair' ? 'wheelchair' : 'standard',
          number_of_passengers: Math.max(0, Number(p?.number_of_passengers ?? p?.count ?? 0) || 0),
        }))
        .filter((p: { number_of_passengers: number }) => p.number_of_passengers > 0);
      if (normalized.length > 0) return normalized;
    }

    const standard = Math.max(0, Number(ride?.standard_seat ?? ride?.numPassengers ?? 0) || 0);
    const wheelchair = Math.max(0, Number(ride?.wheelchair ?? 0) || 0);
    const fallback: Array<{ type: string; number_of_passengers: number }> = [];
    if (standard > 0) fallback.push({ type: 'standard', number_of_passengers: standard });
    if (wheelchair > 0) fallback.push({ type: 'wheelchair', number_of_passengers: wheelchair });
    if (fallback.length === 0) fallback.push({ type: 'standard', number_of_passengers: 1 });
    return fallback;
  };

  const normalizeRequestTimeForImport = (value: unknown): string => {
    if (typeof value !== 'string' || value.trim().length === 0) return getDefaultRequestTime();
    const trimmed = value.trim();
    // Backend import endpoint is sensitive to timezone suffixes (+00:00 / Z) in some environments.
    // Keep the same clock time but strip trailing timezone designator.
    return trimmed.replace(/(Z|[+-]\d{2}:\d{2})$/i, '');
  };

  return rides.map((ride) => {
    const rideId = asNonEmptyIdString(ride.ride_id) ?? asNonEmptyIdString(ride.rideId);
    const externalRef =
      asNonEmptyIdString(ride.external_reference_id) ??
      asNonEmptyIdString(ride.externalReferenceId) ??
      rideId ??
      externalRefFallback();
    const userRef =
      asNonEmptyIdString(ride.external_user_reference_id) ??
      asNonEmptyIdString(ride.externalUserReferenceId) ??
      asNonEmptyIdString(ride.user_id) ??
      asNonEmptyIdString(ride.userId) ??
      externalRefFallback();

    const rideIdForApi = includeRideId && rideId && validateUuid(rideId) ? rideId : undefined;
    const fromLat = asFiniteNumber(ride.from_latitude) ?? 0;
    const fromLng = asFiniteNumber(ride.from_longitude) ?? 0;
    const toLat = asFiniteNumber(ride.to_latitude) ?? 0;
    const toLng = asFiniteNumber(ride.to_longitude) ?? 0;

    return {
      ...(rideIdForApi ? { ride_id: rideIdForApi } : {}),
      external_reference_id: externalRef,
      external_user_reference_id: userRef,
      from_latitude: fromLat,
      from_longitude: fromLng,
      to_latitude: toLat,
      to_longitude: toLng,
      from_address: ride.from_address || '',
      to_address: ride.to_address || '',
      request_type: ride.request_type || 'departure_fixed',
      passengers: normalizePassengers(ride),
      batch_id: sharedBatchId,
      request_time: normalizeRequestTimeForImport(ride.request_time),
    };
  });
}

/**
 * Build request body for conductor-config endpoint (same as httm_interface).
 * Uses snake_case: config_name, iteration_count, travel_time_multiplier.
 * Does not send id; the API returns { status, id }.
 * Accepts either { id, configuration } or { problem_config } (full sim config).
 */
function buildConductorConfigPayload(problemConfig: any): Record<string, unknown> {
  if (!problemConfig) return {};
  const config = problemConfig.configuration ?? problemConfig.problem_config?.configuration ?? problemConfig.problem_config ?? {};
  const payload: Record<string, unknown> = {
    config_name: config.config_name ?? 'default',
    iteration_count: config.iterationCount ?? config.iteration_count ?? 200,
    travel_time_multiplier: config.travelTimeMultiplier ?? config.travel_time_multiplier ?? 1.1,
    detailed_path_required: true,
    pickupProcessingTimeOverrideSeconds: config.pickupProcessingTimeOverrideSeconds ?? 30,
    dropoffProcessingTimeOverrideSeconds: config.dropoffProcessingTimeOverrideSeconds ?? 30,
  };
  const vttm = config.variableTravelTimeMultipliers ?? config.variable_travel_time_multipliers;
  if (Array.isArray(vttm) && vttm.length > 0) {
    payload.variable_travel_time_multipliers = vttm;
  }
  return payload;
}

export async function createProblem(problemConfig: any): Promise<{ problem_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Problem Creation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const payload = buildConductorConfigPayload(problemConfig);
    const fallbackId = problemConfig?.id ?? problemConfig?.problem_config?.id;
    // conductor-config returns { status: "Success - created conductor_problem_config", id: "<uuid>" }
    loggingService.debug('[conductor-config] TTM sent', {
      category: 'SimulationAPI',
      travel_time_multiplier: payload.travel_time_multiplier,
      payload,
    });

    const response = await axios.post(`${API_BASE_URL}/conductor-config`, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    
    loggingService.debug('Conductor-config response', {
      category: 'SimulationAPI',
      status: response.status,
      data: response.data,
    });
    
    if (response.status === 204) {
      loggingService.debug('Received 204 - using fallback problem ID from request', { category: 'SimulationAPI' });
      return { problem_id: fallbackId ?? '' };
    }
    
    if (response.status !== 200) {
      throw new AppError({
        what: 'Problem Creation Failed',
        why: `API error: ${response.status} - ${JSON.stringify(response.data)}`,
        howToFix: 'Check the network connection and API logs. Ensure the problem configuration is valid.',
        category: 'API',
        details: response.data
      });
    }
    
    const data = response.data;
    if (data == null) {
      loggingService.debug('No response data - using fallback problem ID from request', { category: 'SimulationAPI' });
      return { problem_id: fallbackId ?? '' };
    }
    if (typeof data === 'string' && data.trim()) {
      return { problem_id: data.trim() };
    }
    if (typeof data === 'object') {
      const id = data.id ?? data.problem_id ?? data.problemId;
      if (id != null) return { problem_id: String(id) };
    }
    loggingService.debug('Response missing id - using fallback problem ID from request', { category: 'SimulationAPI' });
    return { problem_id: fallbackId ?? '' };
  } catch (error: any) {
    loggingService.error('createProblem failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Problem Creation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function createManyVehicles(vehicles: any[], problemId: string): Promise<{ success: boolean }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Vehicle Creation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/conductor/create_many_vehicles/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
      body: JSON.stringify({ problem_id: problemId, vehicles }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Vehicle Creation Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the vehicle configuration is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    return { success: true };
  } catch (error: any) {
    loggingService.error('createManyVehicles failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Vehicle Creation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function createBulkRides(rides: any[], problemId: string): Promise<{ success: boolean }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Ride Upload Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/conductor/create_bulk_rides/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
      body: JSON.stringify({ problem_id: problemId, rides }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Ride Upload Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the ride data is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    return { success: true };
  } catch (error: any) {
    loggingService.error('createBulkRides failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Ride Upload Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function solveProblem(problemId: string): Promise<{ results_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Simulation Start Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/conductor/solve_problem/${problemId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Simulation Start Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the problem ID is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    const data = await response.json();
    if (!data.results_id) {
      throw new AppError({
        what: 'Simulation Start Failed',
        why: 'API did not return a results ID for the started simulation.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    return { results_id: data.results_id };
  } catch (error: any) {
    loggingService.error('solveProblem failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Simulation Start Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function getSolution(resultsId: string): Promise<any> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Failed to Fetch Simulation Results',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/integration/solution/${resultsId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Failed to Fetch Simulation Results',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the results ID is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    return await response.json();
  } catch (error: any) {
    loggingService.error('getSolution failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Failed to Fetch Simulation Results Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function createScenario(scenario: any): Promise<{ scenario_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Scenario Creation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/simulation-scenario/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
      body: JSON.stringify(scenario),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Scenario Creation Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the scenario configuration is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    
    loggingService.debug('Scenario creation response', {
      category: 'SimulationAPI',
      status: response.status,
      headers: response.headers,
    });
    
    const data = await response.json();
    loggingService.debug('Scenario creation response data', { category: 'SimulationAPI', data });
    
    // Check for different possible field names
    if (data.scenario_id) {
      return { scenario_id: data.scenario_id };
    } else if (data.id) {
      return { scenario_id: data.id };
    } else if (data.scenarioId) {
      return { scenario_id: data.scenarioId };
    } else if (response.status === 200 || response.status === 201) {
      // If successful but no ID returned, generate one or use a placeholder
      loggingService.warn('No scenario_id returned from API, but request was successful', { category: 'SimulationAPI' });
      return { scenario_id: 'scenario-created-successfully' };
    } else {
      throw new AppError({
        what: 'Scenario Creation Failed',
        why: 'API did not return a scenario ID upon successful creation.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
  } catch (error: any) {
    loggingService.error('createScenario failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Scenario Creation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function calculateTimeWindows(timeWindowConfig: any): Promise<{ time_window_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Time Window Calculation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/conductor/calculate_time_windows/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
      body: JSON.stringify(timeWindowConfig),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Time Window Calculation Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the time window configuration is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    const data = await response.json();
    if (!data.time_window_id) {
      throw new AppError({
        what: 'Time Window Calculation Failed',
        why: 'API did not return a time window ID.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    return { time_window_id: data.time_window_id };
  } catch (error: any) {
    loggingService.error('calculateTimeWindows failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Time Window Calculation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function uploadRides(rides: any[], ridesName: string): Promise<{ batch_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Ride Upload Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    loggingService.debug(`Uploading ${rides.length} rides`, { category: 'SimulationAPI', ridesName });

    const sharedBatchId = randomUuidString();
    const formattedRides = formatRidesForImportRidesUpload(rides, { sharedBatchId });

    loggingService.debug('Sending formatted rides', {
      category: 'SimulationAPI',
      count: formattedRides.length,
      sharedBatchId,
    });
    
    const response = await axios.post(`${API_BASE_URL}/data_generator/import_rides/${encodeURIComponent(ridesName)}`, formattedRides, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    if (response.status !== 200) {
      throw new AppError({
        what: 'Ride Upload Failed',
        why: `API error: ${response.status} - ${JSON.stringify(response.data)}`,
        howToFix: 'Check the network connection and API service status. Ensure the ride data is correctly formatted.',
        category: 'API',
        details: response.data
      });
    }
    const data = response.data;
    loggingService.debug('Upload response', { category: 'SimulationAPI', data });
    if (!data.id) {
      throw new AppError({
        what: 'Ride Upload Failed',
        why: 'API did not return a batch ID for the uploaded rides.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    return { batch_id: data.id };
  } catch (error: any) {
    loggingService.error('uploadRides failed', { category: 'SimulationAPI', error });
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const responseData = error.response?.data;
      throw new AppError({
        what: 'Ride Upload Failed',
        why: `API error${status ? ` ${status}` : ''}${responseData ? ` - ${typeof responseData === 'string' ? responseData : JSON.stringify(responseData)}` : ''}`,
        howToFix: 'Check ride rows for valid coordinates, request_time, and passenger fields. Re-import the simulation rides if needed.',
        category: 'API',
        details: responseData ?? error.message,
      });
    }
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Ride Upload Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function createVehicles(vehiclesConfig: any): Promise<{ vehicles_batch_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Vehicle Creation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/conductor/create_vehicles/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
      body: JSON.stringify(vehiclesConfig),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Vehicle Creation Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the vehicle configuration is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    const data = await response.json();
    if (!data.vehicles_batch_id) {
      throw new AppError({
        what: 'Vehicle Creation Failed',
        why: 'API did not return a vehicles batch ID.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    return { vehicles_batch_id: data.vehicles_batch_id };
  } catch (error: any) {
    loggingService.error('createVehicles failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Vehicle Creation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

// New endpoints for simulation integration

export async function runSimulation(
  scenarioId: string,
  algorithm: 'hot_tub' | 'pasha_bulka',
  pashaSettings?: PashaBulkaRunSettings
): Promise<{ task_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Simulation Execution Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const endpoint = algorithm === 'pasha_bulka' 
      ? `/pasha_bulka_simulation/run/${scenarioId}`
      : `/hot_tub_time_machine/run/${scenarioId}`;
    
    const simulationConfig: Record<string, unknown> = {
      algorithm,
      scenario_id: scenarioId,
    };
    if (algorithm === 'pasha_bulka' && pashaSettings) {
      if (pashaSettings.matcher_flags != null) {
        simulationConfig.matcher_flags = pashaSettings.matcher_flags;
      }
      if (pashaSettings.clustering != null) {
        simulationConfig.clustering = pashaSettings.clustering;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
      body: JSON.stringify(simulationConfig),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Simulation Execution Failed',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the scenario ID and algorithm are valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    const data = await response.json();
    if (!data.task_id) {
      throw new AppError({
        what: 'Simulation Execution Failed',
        why: 'API did not return a task ID for the simulation job.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    return { task_id: data.task_id };
  } catch (error: any) {
    loggingService.error('runSimulation failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Simulation Execution Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function getTaskStatus(taskId: string): Promise<any> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Failed to Fetch Task Status',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new AppError({
        what: 'Failed to Fetch Task Status',
        why: `API error: ${response.status} - ${errorText}`,
        howToFix: 'Check the network connection and API service status. Ensure the task ID is valid.',
        category: 'API',
        details: { status: response.status, responseText: errorText }
      });
    }
    
    const responseText = await response.text();
    loggingService.debug('getTaskStatus raw response', { category: 'SimulationAPI', responseText });
    
    // Parse the JSON response
    const data = JSON.parse(responseText);
    loggingService.debug('getTaskStatus parsed', {
      category: 'SimulationAPI',
      data,
      type: typeof data,
      keys: Object.keys(data || {}),
    });
    
    // Return the parsed data object, not the string
    return data;
  } catch (error: any) {
    loggingService.error('getTaskStatus failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Failed to Fetch Task Status Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function generateFleet(fleetConfig: any, fleetName: string): Promise<{ fleet_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Fleet Generation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    // Ensure fleet config is in the correct format expected by the API
    const formattedFleetConfig = {
      vehicles: fleetConfig.vehicles || fleetConfig
    };
    
    loggingService.debug('Fleet generation', {
      category: 'SimulationAPI',
      fleetName,
      fleetConfig,
      formattedFleetConfig,
    });
    
    const response = await axios.post(`${API_BASE_URL}/data_generator/generate_fleet/${encodeURIComponent(fleetName)}`, formattedFleetConfig, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    
    loggingService.debug('Fleet generation response', {
      category: 'SimulationAPI',
      status: response.status,
      data: response.data,
    });
    
    if (response.status !== 200) {
      throw new AppError({
        what: 'Fleet Generation Failed',
        why: `API error: ${response.status} - ${JSON.stringify(response.data)}`,
        howToFix: 'Check the network connection and API service status. Ensure the fleet configuration is valid.',
        category: 'API',
        details: response.data
      });
    }
    const data = response.data;
    if (!data.id) {
      throw new AppError({
        what: 'Fleet Generation Failed',
        why: 'API did not return a fleet ID.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    return { fleet_id: data.id };
  } catch (error: any) {
    loggingService.error('generateFleet failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Fleet Generation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

export async function calculateTimeWindowsWithUserAuth(timeWindowConfig: any, timeWindowName: string): Promise<{ time_window_batch_id: string }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Time Window Calculation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    loggingService.debug('Time window config being sent', { category: 'SimulationAPI', timeWindowConfig });
    
    const response = await axios.post(`${API_BASE_URL}/data_generator/calculate_time_windows_with_user_auth/${encodeURIComponent(timeWindowName)}`, timeWindowConfig, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    if (response.status !== 200) {
      throw new AppError({
        what: 'Time Window Calculation Failed',
        why: `API error: ${response.status} - ${JSON.stringify(response.data)}`,
        howToFix: 'Check the network connection and API service status. Ensure the time window configuration is valid.',
        category: 'API',
        details: response.data
      });
    }
    const data = response.data;
    if (!data.task_id) {
      throw new AppError({
        what: 'Time Window Calculation Failed',
        why: 'API did not return a task ID for the time window calculation job.',
        howToFix: 'This may indicate an issue with the API service. Please contact support.',
        category: 'API',
        details: data
      });
    }
    // This is a queued task, so we return the task_id as the time_window_batch_id
    // The actual time_window_batch_id will be available when the task completes
    return { time_window_batch_id: data.task_id };
  } catch (error: any) {
    loggingService.error('calculateTimeWindowsWithUserAuth failed', {
      category: 'SimulationAPI',
      error,
      responseStatus: error.response?.status,
      responseData: error.response?.data,
      responseHeaders: error.response?.headers,
      request: error.request,
      message: error.message,
    });

    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Time Window Calculation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}

// Alternative endpoint for uploading rides (conductor endpoint)
export async function createBulkRidesConductor(rides: any[]): Promise<{ success: boolean }> {
  const tokenObj = getToken();
  if (!tokenObj) throw new AppError({
    what: 'Bulk Ride Creation Failed',
    why: 'User not authenticated.',
    howToFix: 'Please log in again.',
    category: 'Authentication'
  });
  try {
    const response = await axios.post(`${API_BASE_URL}/conductor/create_bulk_rides/`, rides, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenObj.access_token}`,
      },
    });
    if (response.status !== 200) {
      throw new AppError({
        what: 'Bulk Ride Creation Failed',
        why: `API error: ${response.status} - ${JSON.stringify(response.data)}`,
        howToFix: 'Check the network connection and API service status. Ensure the ride data is correctly formatted.',
        category: 'API',
        details: response.data
      });
    }
    return { success: true };
  } catch (error: any) {
    loggingService.error('createBulkRidesConductor failed', { category: 'SimulationAPI', error });
    if (error instanceof AppError) {
      throw error;
    }
    throw new AppError({
      what: 'Bulk Ride Creation Failed Unexpectedly',
      why: error.message || 'An unknown network or server error occurred.',
      howToFix: NETWORK_OR_ACCESS_HOW_TO_FIX,
      category: 'Network',
      details: error
    });
  }
}
